import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';

interface Notification {
  id: string;
  type: 'contribution' | 'approval' | 'reminder' | 'system' | 'payment_submitted' | 'payment_approved' | 'payment_rejected' | 'payment_pending' | 'new_contribution' | 'contribution_approved' | 'contribution_rejected' | 'contribution_pending' | 'member_joined' | 'member_left' | 'loan_approved' | 'loan_repayment' | 'member_added' | 'admin_assigned';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  related_entity_id?: string;
}

const NotificationIndicator = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const menuRef = useRef<HTMLDivElement>(null);
  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (user) {
      fetchNotifications();
      setupRealtimeSubscription();
      
      // Setup connectivity listener to refresh notifications when back online
      const handleOnline = () => {
        console.log('App back online, refreshing notifications...');
        fetchNotifications();
      };
      window.addEventListener('online', handleOnline);
      
      return () => {
        window.removeEventListener('online', handleOnline);
      };
    }
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const formattedNotifications: Notification[] = (data || []).map((n: any) => ({
        id: n.id,
        type: mapNotificationType(n.type),
        title: n.title,
        message: n.message,
        timestamp: new Date(n.created_at),
        read: n.is_read || false,
        related_entity_id: n.related_entity_id,
      }));

      setNotifications(formattedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!user) return;

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as any;
          const formatted: Notification = {
            id: newNotification.id,
            type: mapNotificationType(newNotification.type),
            title: newNotification.title,
            message: newNotification.message,
            timestamp: new Date(newNotification.created_at),
            read: newNotification.is_read || false,
            related_entity_id: newNotification.related_entity_id,
          };
          setNotifications((prev) => [formatted, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updated = payload.new as any;
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === updated.id
                ? { ...n, read: updated.is_read || false }
                : n
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const mapNotificationType = (type: string): Notification['type'] => {
    const validTypes: Notification['type'][] = [
      'payment_submitted', 'payment_approved', 'payment_rejected', 'payment_pending',
      'contribution', 'approval', 'reminder', 'system',
      'new_contribution', 'contribution_approved', 'contribution_rejected', 'contribution_pending',
      'member_joined', 'member_left',
      'loan_approved', 'loan_repayment', 'member_added', 'admin_assigned'
    ];
    
    if (validTypes.includes(type as Notification['type'])) {
      return type as Notification['type'];
    }
    return 'system';
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'contribution':
        return 'DollarSign';
      case 'approval':
        return 'CheckCircle';
      case 'reminder':
        return 'Bell';
      case 'payment_submitted':
      case 'payment_pending':
        return 'Clock';
      case 'payment_approved':
        return 'CheckCircle';
      case 'payment_rejected':
        return 'XCircle';
      case 'new_contribution':
      case 'contribution_approved':
      case 'contribution_rejected':
      case 'contribution_pending':
        return 'DollarSign';
      case 'member_joined':
      case 'member_left':
      case 'member_added':
        return 'Users';
      case 'loan_approved':
      case 'loan_repayment':
        return 'CreditCard';
      case 'admin_assigned':
        return 'UserCheck';
      case 'system':
        return 'Info';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (type: Notification['type']) => {
    switch (type) {
      case 'contribution':
        return 'var(--color-success)';
      case 'approval':
        return 'var(--color-warning)';
      case 'reminder':
        return 'var(--color-primary)';
      case 'payment_submitted':
      case 'payment_pending':
        return 'var(--color-warning)';
      case 'payment_approved':
        return 'var(--color-success)';
      case 'payment_rejected':
        return 'var(--color-destructive)';
      case 'new_contribution':
      case 'contribution_approved':
        return 'var(--color-success)';
      case 'contribution_rejected':
        return 'var(--color-destructive)';
      case 'contribution_pending':
        return 'var(--color-warning)';
      case 'member_joined':
      case 'member_added':
        return 'var(--color-success)';
      case 'member_left':
        return 'var(--color-muted-foreground)';
      case 'loan_approved':
      case 'loan_repayment':
        return 'var(--color-primary)';
      case 'admin_assigned':
        return 'var(--color-warning)';
      case 'system':
        return 'var(--color-muted-foreground)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getNotificationPath = (notification: Notification): string | null => {
    switch (notification.type) {
      case 'payment_submitted':
        // Super admin should go to payment processing
        if (userProfile?.role === 'super_admin') {
          return '/payment-processing';
        }
        return null;
      
      case 'payment_approved':
      case 'payment_rejected':
        // Group admin should go to subscription management
        if (userProfile?.role === 'group_admin' || userProfile?.role === 'member') {
          return '/subscription-management';
        }
        return null;
      
      case 'payment_pending':
        // Check if this is a contribution approval notification (title contains "Saving Approval")
        // or if it's a subscription payment notification
        if (notification.title && notification.title.includes('Saving Approval')) {
          // This is a contribution approval notification - route to contribution recording
          // Allow for group_admin, super_admin, or assigned admins (group_members.role='admin')
          const isAdmin = userProfile?.role === 'group_admin' || 
                         userProfile?.role === 'super_admin' || 
                         userProfile?.group_roles?.includes('admin');
          if (isAdmin) {
            // Include contribution ID and notification flag in URL
            const params = new URLSearchParams();
            if (notification.related_entity_id) {
              params.set('contributionId', notification.related_entity_id);
            }
            params.set('from', 'notification');
            return `/contribution-recording?${params.toString()}`;
          }
          return null;
        }
        // Otherwise, it's a subscription payment notification
        if (userProfile?.role === 'group_admin' || userProfile?.role === 'member') {
          return '/subscription-management';
        }
        return null;
      
      case 'contribution':
      case 'approval':
      case 'new_contribution':
      case 'contribution_approved':
      case 'contribution_rejected':
      case 'contribution_pending':
        // Go to contribution recording page
        return '/contribution-recording';
      
      case 'member_joined':
      case 'member_left':
      case 'member_added':
      case 'admin_assigned':
        // Go to group management
        if (userProfile?.role === 'group_admin' || userProfile?.role === 'super_admin') {
          return '/group-management';
        }
        return '/member-dashboard';
      
      case 'loan_approved':
      case 'loan_repayment':
        // Go to loan management
        if (userProfile?.role === 'group_admin' || userProfile?.role === 'super_admin') {
          return '/admin-dashboard';
        }
        return '/member-dashboard';
      
      case 'reminder':
        // Go to dashboard
        return userProfile?.role === 'super_admin' 
          ? '/super-admin-dashboard'
          : userProfile?.role === 'group_admin'
          ? '/admin-dashboard'
          : '/member-dashboard';
      
      case 'system':
      default:
        return null;
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      console.log('Notification clicked:', notification.type, 'User role:', userProfile?.role);
      
      // Mark as read
      await handleMarkAsRead(notification.id);
      
      // Close the notification panel
      setIsOpen(false);
      
      // Navigate to the appropriate page
      const path = getNotificationPath(notification);
      console.log('Navigation path:', path);
      
      if (path) {
        navigate(path);
      } else {
        console.warn('No navigation path found for notification type:', notification.type);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', id);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;

      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-sm hover:bg-muted transition-interactive"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={20} className="text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs font-medium rounded-full flex items-center justify-center pulse-indicator">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-popover border border-border rounded-md shadow-elevation-2 z-1050">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-popover-foreground">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-primary hover:text-primary/80 transition-interactive font-medium"
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="text-muted-foreground">Loading notifications...</div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Icon name="Bell" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`
                      p-4 transition-interactive cursor-pointer
                      ${notification.read ? 'bg-popover' : 'bg-primary/5'}
                      hover:bg-muted
                    `}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${getNotificationColor(notification.type)}15` }}
                      >
                        <Icon
                          name={getNotificationIcon(notification.type)}
                          size={16}
                          color={getNotificationColor(notification.type)}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="text-sm font-medium text-popover-foreground">
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {notification.message}
                        </p>
                        <span className="text-xs text-muted-foreground">
                          {formatTimestamp(notification.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t border-border">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
                className="w-full text-sm text-primary hover:text-primary/80 transition-interactive font-medium"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationIndicator;